import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import ImageInput from "@/components/ImageInput";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [loading, setLoading] = useState(false);

  // Pull current settings from server when this page mounts (admin freshness)
  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/settings", {
        team_name: form.team_name || "",
        team_number: form.team_number || "",
        logo_url: form.logo_url || "",
        tagline: form.tagline || "",
      });
      toast.success("Settings saved. Site updated.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <div className="overline mb-3">// Site</div>
      <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-2">Settings</h1>
      <p className="text-voltage-muted text-sm mb-10 font-mono">
        These values are used across the public site — header, hero, footer, and contact section.
      </p>

      <form onSubmit={save} className="voltage-card p-8 space-y-6 max-w-3xl">
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Team Name" value={form.team_name} onChange={(v) => set("team_name", v)} testid="settings-team-name" required />
          <Field label="Team Number" value={form.team_number} onChange={(v) => set("team_number", v)} testid="settings-team-number" placeholder="e.g. 12345" />
        </div>

        <div>
          <label className="overline mb-2 block">Tagline</label>
          <input
            type="text"
            value={form.tagline || ""}
            onChange={(e) => set("tagline", e.target.value)}
            data-testid="settings-tagline"
            placeholder="Strike with Voltage. Engineer the Future."
            className="w-full bg-black/50 border border-voltage-blue/40 focus:border-voltage-yellow outline-none px-3 py-2 text-white font-body"
          />
          <p className="text-xs text-voltage-muted font-mono mt-1">Shown on the hero section.</p>
        </div>

        <div>
          <label className="overline mb-2 block">Team Logo</label>
          <ImageInput
            value={form.logo_url}
            onChange={(v) => set("logo_url", v)}
            testid="settings-logo"
            maxWidth={800}
            quality={0.92}
          />
          <p className="text-xs text-voltage-muted font-mono mt-1">Shown in the header, hero, and email notifications.</p>
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={loading}
            data-testid="settings-save"
            className="btn-primary clip-bolt disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {loading ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, testid, placeholder }) {
  return (
    <div>
      <label className="overline mb-2 block">{label}{required && " *"}</label>
      <input
        type="text"
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        placeholder={placeholder}
        className="w-full bg-black/50 border border-voltage-blue/40 focus:border-voltage-yellow outline-none px-3 py-2 text-white font-body"
      />
    </div>
  );
}
